import { faChevronRight, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DashboardsIcon } from 'App/dashboards/_components/DashboardsIcon'
import { useAccessibleDashboards } from 'App/dashboards/_utils/useAccessibleDashboards'
import { translate } from 'i18n/i18n'
import { useMemo } from 'react'
import { colors, dashboardColors } from 'settings/colors'
import { alphabeticalSort } from 'utils/alphabeticalSort'
import { pushRoute } from 'utils/pushRoute'

export const AllDashboards = () => {
  const accessibleDashboards = useAccessibleDashboards()

  // List only dashboards the user can actually use; useAccessibleDashboards applies each dashboard's
  // own access check (family-agnostic), shared with the side-nav entry so the two can't diverge.
  const visibleDashboards = useMemo(
    () =>
      [...accessibleDashboards].sort((a, b) =>
        // Alphabetical by the displayed (translated) title; app-wide collator (numeric +
        // ignorePunctuation). Sorts a computed value, so alphabeticalSort rather than sortByKey.
        alphabeticalSort(a.getTitle(), b.getTitle()),
      ),
    [accessibleDashboards],
  )

  return (
    <div
      css={{
        background: colors.white,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '16px 24px',
          borderBottom: `1px solid ${colors.grey200}`,
        }}
      >
        <button
          aria-label={translate.phrases.banyanApp('Close')}
          onClick={() => pushRoute('/map')}
          css={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            color: colors.midnight,
          }}
        >
          <FontAwesomeIcon icon={faXmark} size="lg" />
        </button>
        <h1 css={{ fontSize: 20, fontWeight: 600, margin: 0, color: colors.midnight }}>
          {translate.phrases.banyanApp('Dashboards')}
        </h1>
      </div>

      <div css={{ padding: 24, background: colors.grey50, flex: 1 }}>
        <div
          css={{
            fontSize: 14,
            fontWeight: 600,
            color: colors.midnight,
            marginBottom: 12,
          }}
        >
          {translate.phrases.banyanApp('All Dashboards')} {`(${visibleDashboards.length})`}
        </div>
        {visibleDashboards.length === 0 ? (
          <div css={{ fontSize: 14, color: dashboardColors.mutedText }}>
            {/* Same access wording as the chart's no-permission state, minus the property. Uses
                placeholder since the property-less variant isn't a registered banyanApp phrase. */}
            {translate.phrases.banyanApp('You do not have access to this feature')}
          </div>
        ) : (
          <div css={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {visibleDashboards.map((d) => (
              <button
                key={d.id}
                onClick={() => pushRoute(`/dashboards/${d.id}`)}
                css={{
                  'background': colors.white,
                  'border': `1px solid ${colors.grey200}`,
                  'borderRadius': 8,
                  'padding': '16px 20px',
                  'display': 'flex',
                  'alignItems': 'center',
                  'gap': 16,
                  'cursor': 'pointer',
                  'textAlign': 'left',
                  '&:hover': { background: colors.grey50 },
                }}
              >
                <div
                  css={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: colors.grey50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // Design grey (#737373) for the outlined dashboard glyph.
                    color: dashboardColors.mutedText,
                  }}
                >
                  <DashboardsIcon variant="outline" size={18} />
                </div>
                <div css={{ flex: 1 }}>
                  <div css={{ fontWeight: 600, color: colors.midnight }}>{d.getTitle()}</div>
                  <div css={{ fontSize: 13, color: colors.grey800, marginTop: 2 }}>{d.getDescription()}</div>
                </div>
                <FontAwesomeIcon icon={faChevronRight} color={colors.grey500} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
